import VueRouter from 'vue-router'
import store from '../store'
import { isUUID, prettyUrlParam } from '../helpers'

// auth
import mlogin from '../components/pages/auth/mlogin.vue'
import login from '../components/pages/auth/login.vue'
import logout from '../components/pages/auth/logout.vue'
import signUp from '../components/pages/auth/sign-up.vue'
import confirmEmail from '../components/pages/auth/confirm-email.vue'
import finishRegistration from '../components/pages/auth/finish-registration.vue'
import forgotPassword from '../components/pages/auth/forgot-password.vue'
import resetPassword from '../components/pages/auth/reset-password.vue'

// exceptions
import forbidden from '../components/pages/exceptions/forbidden.vue'
import noResponse from '../components/pages/exceptions/no-response.vue'
import notFound from '../components/pages/exceptions/not-found.vue'
import whoops from '../components/pages/exceptions/whoops.vue'

// pages
import home from '../components/pages/home'
import profile from '../components/pages/profile'

// 'admin' routes
import organizations from '../components/pages/admin/organizations'
import editOrganization from '../components/pages/admin/organizations/edit'
import users from '../components/pages/admin/users'
import modules from '../components/pages/admin/modules'
import editModule from '../components/pages/admin/modules/edit'
import components from '../components/pages/admin/components'
import editComponent from '../components/pages/admin/components/edit'
import topics from '../components/pages/admin/topics'
import editTopic from '../components/pages/admin/topics/edit'
import items from '../components/pages/admin/items'
import media from '../components/pages/admin/media'
import editMedia from '../components/pages/admin/media/edit'
import groups from '../components/pages/admin/groups'
import editGroup from '../components/pages/admin/groups/edit'
import members from '../components/pages/admin/members'
import editMember from '../components/pages/admin/members/edit'
import accessRequests from '../components/pages/admin/access-requests'
import suggestions from '../components/pages/admin/suggestions'
import viewSuggestion from '../components/pages/admin/suggestions/view'
import reports from '../components/pages/admin/reports'
import bioreq from '../components/pages/admin/bioreq'

// 'client' routes
import contentTree from '../components/pages/client/content-tree'
import modulesClient from '../components/pages/client/modules'
import componentsClient from '../components/pages/client/components'
import topicsClient from '../components/pages/client/topics'
import itemsClient from '../components/pages/client/items'
import starredTopics from '../components/pages/client/starred-topics'
import bioreqClient from '../components/pages/client/bioreq'

// 'about' routes
import about from '../components/pages/about/index.vue'

import knowledgecheckRoutes from './modules/knowledgechecks'

const routes = [
  // exceptions
  {
    path: '/whoops',
    name: 'whoops',
    component: whoops
  },
  {
    path: '/no-response',
    name: 'no-response',
    component: noResponse
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: forbidden
  },
  {
    path: '/not-found',
    name: 'not-found',
    component: notFound
  },

  // about
  {
    path: '/about',
    name: 'about',
    component: about
  },

  // mobile auth 
  {
    path: '/mlogin', 
    name: 'mlogin', 
    component: mlogin
  },

  // guest
  {
    path: '/login',
    name: 'login',
    component: login,
    meta: {
      guest: true
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: logout
  },
  {
    path: '/sign-up/',
    name: 'sign-up',
    component: signUp,
    meta: {
      guest: true
    }
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: resetPassword,
    meta: {
      guest: true
    }
  },
  {
    path: '/finish-registration/:token',
    name: 'finish-registration',
    component: finishRegistration,
    meta: {
      guest: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: forgotPassword,
    meta: {
      guest: true
    }
  },

  // guest and authenticated users can confirm email
  {
    path: '/confirm-email/:token',
    name: 'confirm-email',
    component: confirmEmail
  },

  // pages
  {
    path: '/',
    name: 'home',
    component: home,
    meta: {
      auth: true
    },
    beforeEnter: async (to, from, next) => {
      const empty = store.getters['client/isEmpty']
      if (empty) {
        await store.dispatch('client/getMe')
      } else {
        if (!store.state.client.fetching) {
          store.dispatch('client/getMe')
        }
      }
      return next()
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: profile,
    meta: {
      auth: true
    }
  },

  // owner only
  {
    path: '/users',
    name: 'users',
    component: users,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/organizations',
    name: 'organizations',
    component: organizations,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/organizations/:organization/edit',
    name: 'edit-organization',
    component: editOrganization,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization',
    redirect: to => {
      return '/:organization/modules'
    },
    name: 'organization-home'
  },

  // 'client' routes
  {
    path: '/:organization/content-tree',
    name: 'content-tree',
    component: contentTree,
    meta: {
      client: true,
      auth: true
    },
    beforeEnter: (to, from, next) => {
      store.dispatch('client/getContentTree')
      return next()
    }
  },
  {
    path: '/:organization/modules',
    name: 'client-modules',
    component: modulesClient,
    meta: {
      client: true,
      auth: true
    }
  },
  {
    path: '/:organization/modules/:module/components',
    name: 'client-components',
    component: componentsClient,
    meta: {
      client: true,
      auth: true
    }
  },
  {
    path: '/:organization/modules/:module/components/:component/topics',
    name: 'client-topics',
    component: topicsClient,
    meta: {
      client: true,
      auth: true
    }
  },
  {
    path: '/:organization/starred-topics',
    name: 'starred-topics',
    component: starredTopics,
    meta: {
      client: true,
      auth: true
    }
  },
  {
    path: '/:organization/bioreq',
    name: 'client-bioreq',
    component: bioreqClient,
    meta: {
      client: true,
      auth: true
    }
  },
  {
    path: '/:organization/modules/:module/components/:component/topics/:topic/items',
    name: 'client-items',
    component: itemsClient,
    meta: {
      client: true,
      auth: true
    },
    beforeEnter: (to, from, next) => {
      if (to.query.dontFetch) {
        return next()
      }

      store.dispatch('items/index', {isAdmin: false})
      return next()
    }
  },

  // 'admin' routes - accessed by 'owners', 'masters' or 'admins'
  {
    path: '/:organization/admin/modules',
    name: 'admin-modules',
    component: modules,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/edit',
    name: 'admin-edit-module',
    component: editModule,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/components',
    name: 'admin-components',
    component: components,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/components/:component/edit',
    name: 'admin-edit-component',
    component: editComponent,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/components/:component/topics',
    name: 'admin-topics',
    component: topics,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/components/:component/topics/:topic/edit',
    name: 'admin-edit-topic',
    component: editTopic,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/modules/:module/components/:component/topics/:topic/items',
    name: 'admin-items',
    component: items,
    meta: {
      auth: true,
      admin: true
    },
    beforeEnter: (to, from, next) => {
      if (to.query.dontFetch) {
        return next()
      }

      store.dispatch('items/index', {isAdmin: true})
      return next()
    }
  },
  {
    path: '/:organization/admin/media',
    name: 'admin-media',
    component: media,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/media/:media/edit',
    name: 'admin-edit-media',
    component: editMedia,
    meta: {
      auth: true,
      admin: true
    }
  },

  // 'master' routes - accessed by 'owners' or 'masters'
  {
    path: '/:organization/admin/reports',
    name: 'admin-reports',
    component: reports,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/groups',
    name: 'admin-groups',
    component: groups,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/groups/:group/edit',
    name: 'admin-edit-group',
    component: editGroup,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/members',
    name: 'admin-members',
    component: members,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/members/:member/edit',
    name: 'admin-edit-member',
    component: editMember,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/access-requests',
    name: 'admin-access-requests',
    component: accessRequests,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/suggestions',
    name: 'admin-suggestions',
    component: suggestions,
    meta: {
      auth: true,
      admin: true
    }
  },
  {
    path: '/:organization/admin/suggestions/:suggestion',
    name: 'admin-view-suggestion',
    component: viewSuggestion,
    meta: {
      auth: true,
      admin: true
    }
  },

  // bioreq routes - accessed by admins in an organization with bioreq access
  {
    path: '/:organization/admin/bioreq',
    name: 'admin-bioreq',
    component: bioreq,
    meta: {
      auth: true,
      admin: true
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: __dirname,
  routes: routes.concat(knowledgecheckRoutes)
})

router.beforeEach(async(to, from, next) => {

  // ---------------------------------------------
  // authenticated / not authenticated redirects
  // ---------------------------------------------

  // redirect to login if not authenticated
  const authenticated = store.getters['token/authenticated']
  if (to.meta.auth && !authenticated) {
    return next('/login')
  }

  // authenticated users can not visit guest routes
  if (to.meta.guest && authenticated) {
    return next(from.path)
  }

  // ---------------------------------------------
  // 1. load data based on the route
  // 2. replace certain ids in url with entity name
  // ---------------------------------------------

  // make sure we have the api config
  if (
    to.meta.auth &&
    authenticated &&
    store.getters['config/isEmpty'] &&
    !store.state.config.fetching
  ) {
    store.dispatch('config/index')
  }

  const updatedParams = {}

  // tenant
  if (to.params.hasOwnProperty('organization')) {
    const current = store.state.tenants.current
    const role = store.getters['client/getCurrentRole']

    // param is id
    if (isUUID(to.params.organization)) {
      const tenantID = to.params.organization

      if (role === 'owner') {
        await setCurrentByID({id: tenantID, namespace: 'tenants'})
      } else {
        if (current === null || current.id !== tenantID) {
          const membership = store.getters['client/getMembershipByID'](tenantID)
          if (membership) {
            store.commit('tenants/setCurrent', membership.tenant)
          } else {
            next({name: 'not-found'})
          }
        }
      }

      updatedParams.organization = prettyUrlParam(store.state.tenants.current.name)

    // param is name
    } else {
      const tenantName = to.params.organization

      if (role === 'owner') {
        await setCurrentByName({name: tenantName, namespace: 'tenants', next})
      } else {
        if (current === null || prettyUrlParam(current.name) !== tenantName) {
          const membership = store.getters['client/getMembershipByName'](tenantName)
          if (membership) {
            store.commit('tenants/setCurrent', membership.tenant)
          } else {
            next({name: 'not-found'})
          }
        }
      }
    }
  }

  // module
  if (to.params.hasOwnProperty('module')) {
    if (isUUID(to.params.module)) {
      await setCurrentByID({id: to.params.module, namespace: 'modules'})
      updatedParams.module = prettyUrlParam(store.state.modules.current.name)
    } else {
      await setCurrentByName({name: to.params.module, namespace: 'modules', next})
    }
  }

  // components
  if (to.params.hasOwnProperty('component')) {
    if (isUUID(to.params.component)) {
      await setCurrentByID({id: to.params.component, namespace: 'components'})
      updatedParams.component = prettyUrlParam(store.state.components.current.name)
    } else {
      await setCurrentByName({name: to.params.component, namespace: 'components', next})
    }
  }

  // topics
  if (to.params.hasOwnProperty('topic')) {
    if (isUUID(to.params.topic)) {
      await setCurrentByID({id: to.params.topic, namespace: 'topics'})
      updatedParams.topic = prettyUrlParam(store.state.topics.current.name)
    } else {
      await setCurrentByName({name: to.params.topic, namespace: 'topics', next})
    }
  }

  // media
  if (to.params.hasOwnProperty('media')) {
    setCurrentByID({id: to.params.media, namespace: 'media'})
  }

  // groups
  if (to.params.hasOwnProperty('group')) {
    setCurrentByID({id: to.params.group, namespace: 'groups'})
  }

  // members
  if (to.params.hasOwnProperty('member')) {
    setCurrentByID({id: to.params.member, namespace: 'members'})
  }

  // suggestions
  if (to.params.hasOwnProperty('suggestion')) {
    setCurrentByID({id: to.params.suggestion, namespace: 'suggestions'})
  }

  // bioreq
  if (to.params.hasOwnProperty('bioreq')) {
    setCurrentByID({id: to.params.bioreq, namespace: 'bioreq'})
  }

  if (Object.keys(updatedParams).length > 0) {
    next({
      name: to.name,
      params: {
        ...to.params,
        ...updatedParams
      }
    })
  }

  // ---------------------------------------------
  // If coming from a route where data is editable,
  // check if there are unsaved changes
  // ---------------------------------------------

  const confirmationMessage = 'There are unsaved changes, are you sure you want to leave?'
  let confirmationResult = true

  switch (from.name) {

  // Edit Organization
  case 'edit-organization':
    if (!isUUID(from.params.organization)) {
      if (store.getters['tenants/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Media
  case 'admin-edit-media':
    if (!isUUID(from.params.media)) {
      if (store.getters['media/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Group
  case 'admin-edit-group':
    if (!isUUID(from.params.group)) {
      if (store.getters['groups/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Member
  case 'admin-edit-member':
    if (!isUUID(from.params.member)) {
      if (store.getters['members/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Module
  case 'admin-edit-module':
    if (!isUUID(from.params.module)) {
      if (store.getters['modules/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Component
  case 'admin-edit-component':
    if (!isUUID(from.params.component)) {
      if (store.getters['components/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Topic
  case 'admin-edit-topic':
    if (!isUUID(from.params.topic)) {
      if (store.getters['topics/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break

  // Edit Items
  case 'admin-items':
    if (!to.query.preview) {
      if (store.getters['items/unsavedChanges']) {
        confirmationResult = window.confirm(confirmationMessage)
      }
    }
    break
  }

  if (confirmationResult) {
    return next()
  }
})

// set the current entity for a namespace
const setCurrentByID = async ({id, namespace}) => {
  const current = store.state[namespace].current

  if (current === null || current.id !== id) {
    // try to find in the index
    const defaultView = store.state[namespace]._defaultView
    const entity = store.state[namespace].views[defaultView].index[id]
    if (entity) {
      store.commit(`${namespace}/setCurrent`, entity)

    // get from api
    } else {
      await store.dispatch(`${namespace}/show`, {id})
    }
  }

  // update for good measure
  store.dispatch(`${namespace}/show`, {id})
}

// set the current entity for a namespace
const setCurrentByName = async({name, namespace, next}) => {
  const current = store.state[namespace].current

  if (current === null || prettyUrlParam(current.name) !== name) {
    // try to find in the index
    const entity = store.getters[`${namespace}/byPrettyUrlName`](name)
    if (entity) {
      store.commit(`${namespace}/setCurrent`, entity)
      store.dispatch(`${namespace}/show`, {id: entity.id}) // update for good measure

    // fetch the index, check again
    } else {
      const defaultView = store.state[namespace]._defaultView
      await store.dispatch(`${namespace}/index`, defaultView)
      const entity = store.getters[`${namespace}/byPrettyUrlName`](name)
      if (entity) {
        store.commit(`${namespace}/setCurrent`, entity)
      } else {
        next({name: 'not-found'})
      }
    }
  }
}

export default router



// WEBPACK FOOTER //
// ./src/routes/index.js